import { useState, useCallback } from 'react';
import { Class, ClassPagination } from '@/actions/data-fetch-classes';
import SettingsSlideModal from '@/components/Modal/SettingsSlideModal';
import ModernClassForm from '@/components/Forms/ModernClassForm';
import { getClasses } from '@/actions/data-fetch-classes';
import { toastMessage } from '@/utils/toast-message';
import ModernClassesTable from './ModernClassesTable';
import ModernSettingHeader from '@/components/settings/ModernSettingHeader';
import { BookOpen } from 'lucide-react';

interface ClassesClientWrapperProps {
  initialData: Class[];
  pagination: ClassPagination;
}

export function ClassesClientWrapper({
  initialData,
  pagination: initialPagination
}: ClassesClientWrapperProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedClass, setSelectedClass] = useState<Class | null>(null);
  const [data, setData] = useState<Class[]>(initialData || []);
  const [currentPagination, setCurrentPagination] = useState<ClassPagination>(initialPagination);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');

  const fetchData = useCallback(async (params: {
    page: number;
    search?: string;
    sort_column?: string;
    sort_order?: 'ASC' | 'DESC';
  }) => {
    try {
      setIsLoading(true);
      const response = await getClasses(params);
      if (response.success && response.data) {
        setData(response.data.data || []);
        setCurrentPagination(response.data.pagination);
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        toastMessage(error.message, 'error');
      } else {
        toastMessage('An unknown error occurred', 'error');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleOpenModal = (classData?: Class) => {
    setSelectedClass(classData || null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedClass(null);
  };

  const handleSuccess = async (updatedClass: Class) => {
    setData(prevData => {
      const classIndex = prevData.findIndex(c => c.id === updatedClass.id);
      if (classIndex !== -1) {
        const newData = [...prevData];
        newData[classIndex] = updatedClass;
        return newData;
      } else {
        return [...prevData, updatedClass];
      }
    });

    await fetchData({
      page: currentPagination.currentPage || 1,
      search: searchTerm,
      sort_column: sortColumn,
      sort_order: sortOrder
    });

    if (!selectedClass) {
      handleCloseModal();
    }

    toastMessage(
      selectedClass ? 'Class updated successfully' : 'Class created successfully',
      'success'
    );
  };

  const handlePageChange = async (page: number) => {
    await fetchData({
      page,
      search: searchTerm,
      sort_column: sortColumn,
      sort_order: sortOrder
    });
  };

  const handleSearch = async (term: string): Promise<void> => {
    setSearchTerm(term);
    await fetchData({
      page: 1,
      search: term,
      sort_column: sortColumn,
      sort_order: sortOrder
    });
  };

  const handleSort = async (column: string, order: 'ASC' | 'DESC'): Promise<void> => {
    setSortColumn(column);
    setSortOrder(order);
    await fetchData({
      page: currentPagination.currentPage || 1,
      search: searchTerm,
      sort_column: column,
      sort_order: order
    });
  };

  const handleDelete = async () => {
    await fetchData({
      page: 1,
      search: searchTerm,
      sort_column: sortColumn,
      sort_order: sortOrder
    });
  };

  return (
    <>
      <ModernSettingHeader
        title="Classes"
        description="Manage school classes, their capacity, and student assignments. Click on any class to edit its details."
        buttonText="Add New Class"
        onClick={() => handleOpenModal()}
        icon={<BookOpen className="w-6 h-6" />}
      />
      <div className="p-6">
        <ModernClassesTable
          initialData={data}
          onEdit={handleOpenModal}
          pagination={currentPagination}
          onPageChange={handlePageChange}
          onSearch={handleSearch}
          onSort={handleSort}
          onDelete={handleDelete}
          isLoading={isLoading}
          searchTerm={searchTerm}
          sortColumn={sortColumn}
          sortOrder={sortOrder}
        />
      </div>

      <SettingsSlideModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={selectedClass ? 'Edit Class' : 'Add New Class'}
        subtitle={selectedClass ? 'Update class details and settings' : 'Create a new class for your school'}
        icon={<BookOpen className="w-6 h-6" />}
      >
        <ModernClassForm
          onClose={handleCloseModal}
          initialData={selectedClass}
          onSuccess={handleSuccess}
        />
      </SettingsSlideModal>
    </>
  );
}
